/**
 * The starting balance on the import's Confirm (TMMAPP-0123 item 17; TMMAPI-0160 U2). Only while the
 * account's opening is pending:
 * - with `suggestedOpening`: "Starting balance: {amount} on {date} ({source})", with **Use this** (the
 *   default) and **Edit** (amount and date);
 * - with `suggestedOpeningIssue`: a warning in catalog copy, and Edit opened.
 *
 * The commit sends `opening` when the person kept or edited it. `OPENING_AFTER_FIRST_LINE` is said at
 * the date field; `OPENING_ALREADY_SET` drops the block silently and the caller commits again without
 * it. The SPA computes nothing: the suggestion and its date are the server's.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError, openingAfterFirstLine } from '@/api/errors';
import type { ImportPreview } from '@/api/imports';
import { MoneyText } from '@/components/MoneyText';
import { applyBackendFields } from '@/features/shared/useFieldErrors';
import { formatCivilDate, minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';

type Mode = 'USE' | 'EDIT';
type FieldErrors = { amount?: string | undefined; on?: string | undefined };

export interface CommitOpening {
  opening?: { amountMinor: number; on: string };
}

export interface ImportOpening {
  /** The block; null when there is nothing to ask. */
  node: ReactNode;
  /**
   * The commit's `opening` part: `{}` when none is sent, null when the fields need fixing (the errors
   * are shown and the first takes focus).
   */
  collect: () => CommitOpening | null;
  /** An opening refusal: `retry` (sent again without it), `shown` (at its field), or null (not ours). */
  handleError: (error: unknown) => 'retry' | 'shown' | null;
}

const CIVIL = /^\d{4}-\d{2}-\d{2}$/;
const SERVER_FIELDS = ['opening.amountMinor', 'opening.on'] as const;

export function useImportOpening({
  pending,
  preview,
  currency,
  disabled,
}: {
  /** The account's opening is pending (`Account.openingPending`). */
  pending: boolean;
  preview: ImportPreview | null;
  currency: string | null;
  disabled: boolean;
}): ImportOpening {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const suggestion = preview?.suggestedOpening ?? null;
  const issue = preview?.suggestedOpeningIssue ?? null;
  const amountRef = useRef<HTMLInputElement>(null);
  const onRef = useRef<HTMLInputElement>(null);
  // A refusal said at the date field takes focus once the field renders enabled (TMMAPP-0125 item 6).
  const focusOn = useRef(false);

  const initial = () => ({
    mode: (issue !== null ? 'EDIT' : 'USE') as Mode,
    amount: suggestion !== null ? minorToInputText(suggestion.amountMinor, currency, locale) : '',
    on: suggestion?.on ?? '',
  });
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [dropped, setDropped] = useState(false);
  // A re-preview that serves another suggestion starts the block again from it.
  const key = `${suggestion?.amountMinor ?? ''}|${suggestion?.on ?? ''}|${suggestion?.source ?? ''}|${issue ?? ''}`;
  const [seenKey, setSeenKey] = useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setForm(initial());
    setErrors({});
  }

  useEffect(() => {
    if (!focusOn.current || errors.on === undefined || disabled) return;
    focusOn.current = false;
    onRef.current?.focus();
  }, [errors, disabled]);

  const shown = pending && !dropped && preview !== null && (suggestion !== null || issue !== null);
  const editing = form.mode === 'EDIT' || suggestion === null;

  function collect(): CommitOpening | null {
    if (!shown) return {};
    if (!editing && suggestion !== null) {
      return { opening: { amountMinor: suggestion.amountMinor, on: suggestion.on } };
    }
    // Left blank: nothing is sent, and the Review step asks for it.
    if (form.amount.trim().length === 0 && form.on === '') return {};
    const next: FieldErrors = {};
    const amountMinor = parseMoneyToMinor(form.amount, currency, locale);
    if (amountMinor === null) next.amount = t('errors.fieldInvalid');
    if (!CIVIL.test(form.on)) next.on = t('errors.fieldInvalid');
    else if (form.on > today) next.on = t('errors.server.notFuture');
    setErrors(next);
    if (next.amount !== undefined) amountRef.current?.focus();
    else if (next.on !== undefined) onRef.current?.focus();
    if (amountMinor === null || next.on !== undefined) return null;
    return { opening: { amountMinor, on: form.on } };
  }

  function handleError(error: unknown): 'retry' | 'shown' | null {
    if (!shown) return null;
    if (isApiError(error) && error.code === ErrorCodes.OPENING_ALREADY_SET) {
      setDropped(true);
      return 'retry';
    }
    const afterFirstLine = openingAfterFirstLine(error);
    if (afterFirstLine !== null) {
      setForm((prev) => ({ ...prev, mode: 'EDIT' }));
      focusOn.current = true;
      setErrors({
        on:
          afterFirstLine.earliestPostedOn !== null
            ? t('accounts.openingAfterFirstLine', {
                date: formatCivilDate(afterFirstLine.earliestPostedOn, locale),
              })
            : t('errors.fieldInvalid'),
      });
      return 'shown';
    }
    if (isApiError(error) && error.code === ErrorCodes.VALIDATION_FAILED) {
      const next: FieldErrors = {};
      const result = applyBackendFields(
        error,
        SERVER_FIELDS,
        (field, message) => {
          if (field === 'opening.amountMinor') next.amount = message;
          else next.on = message;
        },
        t,
      );
      if (result.placed === 0) return null;
      setForm((prev) => ({ ...prev, mode: 'EDIT' }));
      setErrors(next);
      return 'shown';
    }
    return null;
  }

  if (!shown) return { node: null, collect, handleError };

  const node = (
    <Box
      data-import-opening=""
      role="group"
      aria-labelledby="import-opening-title"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography id="import-opening-title" variant="h4" component="h3">
        {t('accounts.openingTitle')}
      </Typography>
      {issue !== null && <Alert severity="warning">{t(`imports.opening.issue.${issue}`)}</Alert>}
      {suggestion !== null && (
        <Typography variant="body2">
          <Trans
            i18nKey="imports.opening.suggested"
            values={{
              date: formatCivilDate(suggestion.on, locale),
              source: t(`accounts.openingSource.${suggestion.source}`),
            }}
            components={{
              amount: (
                <MoneyText minor={suggestion.amountMinor} currency={currency} variant="body2" />
              ),
            }}
          />
        </Typography>
      )}
      {suggestion !== null && (
        <RadioGroup
          row
          aria-labelledby="import-opening-title"
          value={form.mode}
          onChange={(_e, value) => setForm((prev) => ({ ...prev, mode: value as Mode }))}
        >
          <FormControlLabel
            value="USE"
            control={<Radio />}
            label={t('imports.opening.use')}
            disabled={disabled}
          />
          <FormControlLabel
            value="EDIT"
            control={<Radio />}
            label={t('imports.opening.edit')}
            disabled={disabled}
          />
        </RadioGroup>
      )}
      {editing && (
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
          <TextField
            id="import-opening-amount"
            label={t('accounts.openingBalanceLabel')}
            value={form.amount}
            inputRef={amountRef}
            disabled={disabled}
            onChange={(event) => {
              const amount = event.target.value;
              setForm((prev) => ({ ...prev, amount }));
              setErrors((prev) => ({ ...prev, amount: undefined }));
            }}
            error={errors.amount !== undefined}
            helperText={errors.amount ?? t('imports.opening.amountHelp')}
            fullWidth
          />
          <TextField
            id="import-opening-on"
            type="date"
            label={t('accounts.openingOnLabel')}
            value={form.on}
            inputRef={onRef}
            disabled={disabled}
            onChange={(event) => {
              const on = event.target.value;
              setForm((prev) => ({ ...prev, on }));
              setErrors((prev) => ({ ...prev, on: undefined }));
            }}
            error={errors.on !== undefined}
            helperText={errors.on ?? t('accounts.openingHelpEdit')}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
            fullWidth
          />
        </Stack>
      )}
    </Box>
  );
  return { node, collect, handleError };
}
